import { createContext, useContext, useEffect, useState, ReactNode, useMemo } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  isLoggedIn: boolean;
  isAdmin: boolean;
  isTestAccount: boolean;
  canBypassPayment: boolean;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  isLoading: true,
  isLoggedIn: false,
  isAdmin: false,
  isTestAccount: false,
  canBypassPayment: false,
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isTestAccount, setIsTestAccount] = useState(false);

  const checkUserRoles = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', userId);
      
      if (error) {
        console.error('Error checking user roles:', error);
        return { admin: false, testAccount: false };
      }
      const roles = (data || []).map((r: any) => r.role);
      return {
        admin: roles.includes('admin'),
        testAccount: roles.includes('test_account'),
      };
    } catch (err) {
      console.error('Error checking user roles:', err);
      return { admin: false, testAccount: false };
    }
  };

  useEffect(() => {
    let isMounted = true;

    // Listener for ONGOING auth changes (does NOT control isLoading)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (!isMounted) return;
        
        setSession(session);
        setUser(session?.user ?? null);

        // Fire and forget - don't await, don't set loading
        if (session?.user) {
          checkUserRoles(session.user.id).then(roles => {
            if (isMounted) {
              setIsAdmin(roles.admin);
              setIsTestAccount(roles.testAccount);
            }
          });
        } else {
          setIsAdmin(false);
          setIsTestAccount(false);
        }
      }
    );

    // INITIAL load (controls isLoading)
    const initializeAuth = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!isMounted) return;

        setSession(session);
        setUser(session?.user ?? null);

        // Fetch roles BEFORE setting loading false
        if (session?.user) {
          const roles = await checkUserRoles(session.user.id);
          if (isMounted) {
            setIsAdmin(roles.admin);
            setIsTestAccount(roles.testAccount);
          }
        }
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    initializeAuth();

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({ 
    user, 
    session, 
    isLoading, 
    isLoggedIn: !!session,
    isAdmin,
    isTestAccount,
    canBypassPayment: isAdmin || isTestAccount,
  }), [user, session, isLoading, isAdmin, isTestAccount]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
